/**
 * Landlords domain API functions (Step 7.1.8).
 *
 * Landlords (Phase D). List, get, create, update, soft-delete (archive).
 * Includes landlord properties, units, property managers, and notes.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  parseAdminErrorBodyText,
} from "./core";

export function fetchAdminLandlords(status = "active") {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return fetch(`${API_BASE_URL}/api/admin/landlords${q}`, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Verwaltungen konnten nicht geladen werden.");
    return res.json();
  });
}

/**
 * GET /api/admin/landlords/picker-options — ``{items, total}`` (C3).
 *
 * ``fetchAdminLandlords`` above returns an unbounded bare array with no
 * search and no total — it cannot page. Rather than widen that contract
 * (several admin pages consume the array), the picker gets its own
 * route. Default ``status="all"`` keeps an archived landlord that is
 * already linked to a report findable; the server ranks archived last.
 */
export function fetchAdminLandlordsPage({
  search,
  status = "all",
  limit,
  offset,
} = {}) {
  const sp = new URLSearchParams();
  if (search != null && String(search).trim()) {
    sp.set("search", String(search).trim());
  }
  if (status) sp.set("status", status);
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/landlords/picker-options${
    qs ? `?${qs}` : ""
  }`;
  return fetch(url, { headers: getApiHeaders() }).then(async (res) => {
    if (!res.ok) {
      throw new Error("Vermieter konnten nicht geladen werden.");
    }
    const data = await res.json();
    if (!data || !Array.isArray(data.items)) {
      throw new Error(
        'Ungültige Antwort von GET /api/admin/landlords/picker-options: erwartet paginierte Antwort mit "items".'
      );
    }
    return {
      items: data.items,
      total: typeof data.total === "number" ? data.total : data.items.length,
    };
  });
}

export function fetchAdminLandlord(id) {
  const base = `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(id)}`;
  const sep = base.includes("?") ? "&" : "?";
  return fetch(`${base}${sep}_=${Date.now()}`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error("Verwaltung konnte nicht geladen werden.");
    }
    return res.json();
  });
}

export function createAdminLandlord(body) {
  return fetch(`${API_BASE_URL}/api/admin/landlords`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error("Verwaltung konnte nicht erstellt werden.");
    return res.json();
  });
}

export function updateAdminLandlord(id, body) {
  return fetch(`${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error("Verwaltung konnte nicht gespeichert werden.");
    return res.json();
  });
}

export function deleteAdminLandlord(id) {
  return fetch(`${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) throw new Error("Verwaltung nicht gefunden.");
      throw new Error("Archivieren fehlgeschlagen.");
    }
    return res.json();
  });
}

export function restoreAdminLandlord(id) {
  return fetch(`${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(id)}/restore`, {
    method: "POST",
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) throw new Error("Verwaltung nicht gefunden.");
      throw new Error("Reaktivieren fehlgeschlagen.");
    }
    return res.json();
  });
}

export function fetchAdminLandlordProperties(landlordId) {
  return fetch(
    `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(landlordId)}/properties`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error("Liegenschaften konnten nicht geladen werden.");
    }
    return res.json();
  });
}

export function fetchAdminLandlordUnits(landlordId) {
  return fetch(
    `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(landlordId)}/units`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error("Units konnten nicht geladen werden.");
    }
    return res.json();
  });
}

export function fetchAdminLandlordPropertyManagers(landlordId) {
  return fetch(
    `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(landlordId)}/property-managers`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error("Bewirtschafter konnten nicht geladen werden.");
    }
    return res.json();
  });
}

export function fetchAdminLandlordNotes(landlordId) {
  return fetch(
    `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(landlordId)}/notes`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return { items: [] };
      throw new Error("Notizen konnten nicht geladen werden.");
    }
    return res.json();
  });
}

export async function createAdminLandlordNote(landlordId, content) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(landlordId)}/notes`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ content }),
    }
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(parseAdminErrorBodyText(text));
  }
  try {
    return text ? JSON.parse(text) : {};
  } catch (e) {
    console.warn("createAdminLandlordNote: unexpected response body", e);
    throw new Error("Notiz konnte nicht gespeichert werden.");
  }
}

export async function updateAdminLandlordNote(landlordId, noteId, content) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/landlords/${encodeURIComponent(landlordId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: "PUT",
      headers: getApiHeaders(),
      body: JSON.stringify({ content }),
    }
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(parseAdminErrorBodyText(text));
  }
  try {
    return text ? JSON.parse(text) : {};
  } catch (e) {
    console.warn("updateAdminLandlordNote: unexpected response body", e);
    throw new Error("Notiz konnte nicht gespeichert werden.");
  }
}
